import { Button, Collapse, Drawer, Empty, Flex, Modal, Skeleton } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { collect } from 'collect.js';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { LinkOutlined } from '@ant-design/icons';
import useScreenType from '../../hooks/context/useScreenType';
import { CompanyOperationalStatus, CompanyTier, FeatureFlags, InvoiceStatus } from '../../services/exports/Constants';
import useHelpers from '../../hooks/context/useHelpers';
import useApiClient from '../../hooks/api/useApiClient';
import { useGetSubscriptionsQuery } from '../../store/api-slices';
import SubscriptionTags from '../../components/billing/SubscriptionTags';
import PricingTiers from '../../components/subscription/PricingTiers';
import ClickToCopy from '../../components/ClickToCopy';

const OnboardingScoreKeys = [
  'is_google_business_available',
  'is_smart_pricing_configured',
  'is_flyers_package_sent',
  'is_menu_fully_configured',
  'has_offers_on_competitor_platforms',
  'is_instagram_story_shared',
  'is_order_system_connected_to_website',
];

const TagType = {
  Primary: 'primary',
  Danger: 'danger',
  Warning: 'warning',
};

function getOnboardingScorePointTagType(data, point) {
  const result = ['has_offers_on_competitor_platforms'].includes(point) ? !data[point] : data[point];

  return result ? TagType.Primary : TagType.Warning;
}

function getPotentialTier(data) {
  if (!data.monthly_sales_other_platforms || !data.monthly_sales_other_platforms[0]) {
    return null;
  }

  const salesOtherPlatforms = data.monthly_sales_other_platforms[0];

  return collect([
    {
      tier: CompanyTier.Vip,
      threshold: 15000,
    },
    {
      tier: CompanyTier.Star,
      threshold: 10000,
    },
    {
      tier: CompanyTier.B,
      threshold: 5000,
    },
    {
      tier: CompanyTier.C,
      threshold: 2500,
    },
    {
      tier: CompanyTier.D,
      threshold: 0,
    },
  ]).first((rank) => salesOtherPlatforms >= rank.threshold).tier;
}

function Tag({ type, className, children }) {
  return (
    <div
      className={classnames(
        'rounded-2xl px-xs py-nano font-semibold text-xs flex items-center h-fit w-fit',
        {
          'text-white bg-brand-primary': type === TagType.Primary,
          'text-white bg-brand-danger': type === TagType.Danger,
          'bg-brand-warning': type === TagType.Warning,
        },
        className,
      )}
    >
      {children}
    </div>
  );
}

function OperationalStatusTag({ status }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:CompanySummaryModal' });

  const tagType =
    {
      [CompanyOperationalStatus.Live]: TagType.Primary,
      [CompanyOperationalStatus.Suspended]: TagType.Danger,
      [CompanyOperationalStatus.Paused]: TagType.Warning,
    }[status] ?? null;

  return <Tag type={tagType}>{t(`operational_status.${status}`)}</Tag>;
}

function SetupFeeInvoiceStatusTag({ status, ...rest }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:CompanySummaryModal' });

  const tagType =
    {
      [InvoiceStatus.Paid]: TagType.Primary,
      [InvoiceStatus.Failed]: TagType.Danger,
    }[status] ?? TagType.Warning;

  return (
    <Tag type={tagType} {...rest}>
      {t(`setup_fee.status.${status}`)}
    </Tag>
  );
}

function Section({ title, withBorder, children }) {
  return (
    <div className={classnames('py-lg', { 'border-t border-b-0 border-x-0 border-solid border-gray-200': withBorder })}>
      {Boolean(title) && <h6 className="mb-lg">{title}</h6>}
      <div className="space-y-md">{children}</div>
    </div>
  );
}

function SectionItem({ title, text, withCopy, children }) {
  return (
    <Flex justify="space-between" gap="large">
      <p className="text-sm text-gray-600">{title}</p>
      {Boolean(text) &&
        (withCopy ? (
          <ClickToCopy textClassName="font-medium text-sm">{text}</ClickToCopy>
        ) : (
          <p className="font-medium text-sm">{text}</p>
        ))}
      {children}
    </Flex>
  );
}

function SubscriptionDetailsModal({ subscription, ...rest }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:CompanySummaryModal' });

  const { formatCurrency } = useHelpers();

  const { product } = subscription.items[0];
  const { price } = subscription.items[0];

  return (
    <Modal open okButtonProps={{ hidden: true }} cancelButtonProps={{ hidden: true }} {...rest}>
      <div className="space-y-md">
        <h3 className="leading-[2rem] font-semibold">{product.name}</h3>
        {Boolean(product.description) && <p className="mb-0 whitespace-pre-line">{product.description}</p>}
        <span className="inline-flex">
          <h2 className="leading-[2.25rem] font-semibold">{formatCurrency(price.amount)}</h2>
          <span className="mt-auto ml-xs h-fit text-gray-500">/{t('labels.month')}</span>
        </span>
        <PricingTiers data={price.tiers} className="space-y-xs" />
      </div>
    </Modal>
  );
}

function CompanySummaryModal({ toggle }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Company:CompanySummaryModal' });
  const { t: tCompanyTier } = useTranslation(null, { keyPrefix: 'Shared:CompanyTiers' });

  const { CurrentCompanyManager, SubscriptionsManager } = useApiClient();
  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const hasMerchantBilling = !!serviceProvider?.enabled_feature_flags?.includes(FeatureFlags.MerchantBilling);

  const { isDesktop } = useScreenType();
  const { formatCurrency, getCurrencySymbol, formatDateTime } = useHelpers();
  const { data: subscriptions, isFetching } = useGetSubscriptionsQuery(
    {
      companyId: company?.id,
      apiManager: SubscriptionsManager,
    },
    { skip: !hasMerchantBilling },
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const potentialTier = useMemo(
    () => (data?.monthly_sales_other_platforms ? getPotentialTier(data) : null),
    [data?.monthly_sales_other_platforms],
  );
  const setupFeeInvoiceStatus = useMemo(() => {
    if (data?.setup_fee_invoice_status) {
      return data?.setup_fee_invoice_status;
    }

    return data?.service_contract?.setup_fee_charged_at ? InvoiceStatus.Processing : InvoiceStatus.Failed;
  }, [data]);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const { success, data } = await CurrentCompanyManager.getSummary();
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setData(data);
  }

  return (
    <Drawer
      open
      onClose={toggle}
      loading={loading}
      size={window.innerWidth * (isDesktop ? 0.5 : 1)}
      title={<h4 className="ml-lg">{company?.name}</h4>}
    >
      {Boolean(data) && (
        <>
          <div className="space-y-md">
            <Flex gap="middle" className="items-center">
              <OperationalStatusTag status={data.operational_status} />
              {data.setup_package_type && (
                <div className="rounded-2xl px-xs py-nano font-semibold text-xs uppercase border border-gray-200 border-solid">
                  {t(`setup_packages.${data.setup_package_type}`)}
                </div>
              )}
              {data.service_contract?.contract_term && (
                <div className="rounded-2xl px-xs py-nano font-semibold text-xs uppercase border border-gray-200 border-solid">
                  {t(`contract_terms.${data.service_contract.contract_term}`)}
                </div>
              )}
            </Flex>
          </div>
          <Section>
            {Boolean(data.google_places_id) && (
              <SectionItem
                title={t('fields.google_profile')}
                text={
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(`${data.name} - ${data.formatted_address}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('buttons.open')} <LinkOutlined className="text-lg" />
                  </a>
                }
              />
            )}
            <SectionItem title={t('fields.owner_name')} text={data.owner_name} withCopy />
            <SectionItem title={t('fields.owner_phone_number')} text={data.owner_phone_number} withCopy />
            <SectionItem title={t('fields.company_phone_number')} text={data.company_phone_number} withCopy />
            <SectionItem title={t('fields.address')} text={data.formatted_address} withCopy />
          </Section>
          <Section title={t('sections.people')} withBorder>
            <SectionItem title={t('fields.onboarding_manager')} text={data.onboarding_manager ?? 'N/A'} />
            <SectionItem title={t('fields.sales_manager')} text={data.sales_manager ?? 'N/A'} />
          </Section>
          <Section title={t('sections.financial_metrics')} withBorder>
            <SectionItem title={t('fields.last_30_days_sales')} text={formatCurrency(data.last_30_days_sales)} />
            <SectionItem title={t('fields.lifetime_sales')} text={formatCurrency(data.lifetime_sales)} />
            <SectionItem
              title={t('fields.monthly_sales_other_platforms')}
              text={
                data.monthly_sales_other_platforms
                  ? collect(data.monthly_sales_other_platforms)
                      .map((item) => `${getCurrencySymbol()}${item / 1000}k`)
                      .implode(' - ') + (data.monthly_sales_other_platforms.length === 1 ? '+' : '')
                  : 'N/A'
              }
            />
            <SectionItem title={t('fields.last_payout')} text={data.payouts.length === 0 && 'N/A'}>
              {data.payouts.length !== 0 && (
                <div>
                  <p className="text-sm font-medium text-right">{formatCurrency(data.payouts[0].amount)}</p>
                  <p className="text-xs font-medium text-gray-600 text-right">
                    {formatDateTime(data.payouts[0].paid_at, { timeFormat: '' })}
                  </p>
                </div>
              )}
            </SectionItem>
          </Section>
          <Section title={t('sections.onboarding_and_quality')} withBorder>
            <SectionItem
              title={t('fields.potential_tier')}
              text={potentialTier ? tCompanyTier(potentialTier) : 'N/A'}
            />
            <SectionItem title={t('fields.current_tier')} text={tCompanyTier(data.current_tier)} />
            <SectionItem title={t('fields.signed_at')} text={formatDateTime(data.signed_at, { timeFormat: '' })} />
            <SectionItem
              title={t('fields.live_from')}
              text={data.live_from ? formatDateTime(data.live_from, { timeFormat: '' }) : '-'}
            />
            {data.onboarding_quality_score ? (
              <Collapse
                ghost
                expandIconPlacement="end"
                items={[
                  {
                    key: '1',
                    classNames: {
                      header: '!p-none',
                      body: '!p-none',
                    },
                    label: (
                      <SectionItem title={t('fields.onboarding_quality_score')} text={data.onboarding_quality_score} />
                    ),
                    children: (
                      <ul className="list-disc space-y-md my-md">
                        {OnboardingScoreKeys.map((item) => (
                          <li key={item}>
                            <SectionItem title={t(`fields.${item}`)}>
                              <Tag type={getOnboardingScorePointTagType(data, item)}>
                                {t(data[item] ? 'labels.yes' : 'labels.no')}
                              </Tag>
                            </SectionItem>
                          </li>
                        ))}
                      </ul>
                    ),
                  },
                ]}
              />
            ) : (
              <SectionItem title={t('fields.onboarding_quality_score')} text="N/A" />
            )}
            <SectionItem
              title={t('fields.screening_date')}
              text={data.screening_date ? formatDateTime(data.screening_date, { timeFormat: '' }) : 'N/A'}
            />
            <SectionItem
              title={t('fields.delivery_mode')}
              text={t(`delivery_modes.${data.delivery_mode}`, { provider: serviceProvider?.name })}
            />
            <SectionItem title={t('fields.has_storefront', { provider: serviceProvider?.name })}>
              <Tag type={data.has_storefront ? TagType.Primary : TagType.Warning}>
                {t(data.has_storefront ? 'labels.yes' : 'labels.no')}
              </Tag>
            </SectionItem>
          </Section>
          {hasMerchantBilling && (
            <Section title={t('sections.subscriptions')} withBorder>
              {isFetching && <Skeleton active loading />}
              {!isFetching &&
                (subscriptions?.data?.length > 0 ? (
                  subscriptions.data.map((item) => (
                    <div
                      className="p-xs border rounded-lg border-solid border-gray-200 space-y-md"
                      key={`subscription-${item.id}`}
                    >
                      <Flex justify="space-between" gap="large">
                        <div className="space-y-xs">
                          <h6>{collect(item.items).implode('product.name', ', ')}</h6>
                          <SubscriptionTags subscription={item} />
                        </div>
                        <span className="text-base font-semibold">{formatCurrency(item.original_amount)}</span>
                      </Flex>
                      {item.items.length === 1 && (item.items[0].price.tiers ?? []).length > 0 && (
                        <Flex justify="center">
                          <Button type="link" onClick={() => setSelectedSubscription(item)}>
                            {t('buttons.view_details')}
                          </Button>
                        </Flex>
                      )}
                    </div>
                  ))
                ) : (
                  <Empty />
                ))}
            </Section>
          )}
          <Section title={t('sections.payments')} withBorder>
            {Boolean(data.setup_fee_invoice_amount) || Boolean(data.service_contract?.total_netto_setup_fee) ? (
              <div className="p-xs border rounded-lg border-solid border-gray-200 flex justify-between">
                <div>
                  <h6>{t('setup_fee.title')}</h6>
                  <span className="text-gray-600">
                    {formatCurrency(data.setup_fee_invoice_amount ?? data.service_contract?.total_netto_setup_fee)}
                  </span>
                </div>
                <SetupFeeInvoiceStatusTag status={setupFeeInvoiceStatus} className="ml-auto" />
              </div>
            ) : (
              <Empty />
            )}
          </Section>
          <Section title={t('sections.last_invoices')} withBorder>
            {data.invoices.length > 0 ? (
              data.invoices.map((item) => (
                <div
                  className="p-xs border rounded-lg border-solid border-gray-200 flex justify-between"
                  key={`invoice-${item.id}`}
                >
                  <div className="space-y-xs">
                    <h6>{item.name}</h6>
                    <span className="text-gray-600">{formatDateTime(item.created_at, { timeFormat: '' })}</span>
                  </div>
                  <div className="space-y-xs">
                    <span className="text-base font-semibold">{formatCurrency(item.total_amount)}</span>
                    <SetupFeeInvoiceStatusTag status={item.status} className="ml-auto" />
                  </div>
                </div>
              ))
            ) : (
              <Empty />
            )}
          </Section>
        </>
      )}
      {selectedSubscription && (
        <SubscriptionDetailsModal subscription={selectedSubscription} onCancel={() => setSelectedSubscription(null)} />
      )}
    </Drawer>
  );
}

export default CompanySummaryModal;
